import { Fragment, useState, useEffect, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Copy, Flame } from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender
} from "@tanstack/react-table";
import PaginationControls from "../boards/PaginationControls";
import type { PaginationMetadata } from "../../types/boards";
import type { MarketListing, MarketView } from "../../types/market";
import "./MarketTable.css";

// Bid/buyout/sold amounts can run into the hundreds of millions or
// billions - "20M"/"1.2B" scans faster than "20,000,000"/"1,200,000,000"
// at a glance. Left under 1M as exact digits, since compact notation on
// small amounts (e.g. "1.1K" for 1,111) loses precision that's still
// easy to read in full.
const formatCompact = (value: number | null) => {
  if (value === null) return "-";
  if (Math.abs(value) < 1_000_000) return value.toLocaleString();
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(value);
};

// Amount colored by currency so the price stands out from the bidder and
// labels around it: gold in yellow, Qp in red.
const Price = ({
  amount,
  currency
}: {
  amount: number | null;
  currency: string | null;
}) => (
  <span className={`market-price market-price--${(currency || "").toLowerCase()}`}>
    {formatCompact(amount)} {currency || ""}
  </span>
);

const HOUR_MS = 60 * 60 * 1000;
// A live listing with this many bids gets the flame badge.
const HOT_BID_COUNT = 5;

// "2d 4h" / "3h 12m" / "9m" - coarse on purpose, the table only re-renders
// every 30s. `urgent` (under an hour) is what the red styling keys off.
const formatTimeLeft = (endsAt: string | null, now: number) => {
  if (!endsAt) return { text: "-", urgent: false };
  const ms = new Date(endsAt).getTime() - now;
  if (ms <= 0) return { text: "Ended", urgent: false };
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  let text: string;
  if (days > 0) text = `${days}d ${hours % 24}h`;
  else if (hours > 0) text = `${hours}h ${minutes % 60}m`;
  else text = minutes > 0 ? `${minutes}m` : "<1m";
  return { text, urgent: ms < HOUR_MS };
};

const formatDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : "-";

// A live auction in its last hour - gets an "Ending soon" label and a red
// row edge, matching the red "Time left".
const isEndingSoon = (listing: MarketListing, now: number) => {
  if (listing.status !== "listed" || !listing.endsAt) return false;
  const ms = new Date(listing.endsAt).getTime() - now;
  return ms > 0 && ms < HOUR_MS;
};

// Only meaningful for Bonus loot weapons - questAverageDamage is only set
// by the backend when category/itemType/averageDam/level all line up (see
// getRecentListings in lightclanApi's market.service.ts), so its presence
// alone is the signal to render this.
const DamageBadge = ({ listing }: { listing: MarketListing }) => {
  if (
    listing.damagePercent === undefined ||
    listing.damageDiff === undefined ||
    listing.questAverageDamage === undefined
  ) {
    return null;
  }
  const isPositive = listing.damageDiff >= 0;
  const sign = isPositive ? "+" : "";
  const tooltip =
    `${listing.averageDam} avg dam vs ${listing.questAverageDamage} quest baseline ` +
    `(level ${listing.level}): ${sign}${Math.round(listing.damageDiff)}`;
  return (
    <span
      className={`market-damage-badge market-tooltip ${
        isPositive ? "market-damage-badge--positive" : "market-damage-badge--negative"
      }`}
      data-tooltip={tooltip}
    >
      {sign}
      {listing.damagePercent.toFixed(1)}%
    </span>
  );
};

const buildCardTooltip = (listing: MarketListing): string | undefined => {
  const lines: string[] = [];
  if (listing.category) lines.push(`Category: ${listing.category}`);
  if (listing.itemType) lines.push(`Type: ${listing.itemType}`);
  if (listing.material) lines.push(`Material: ${listing.material}`);
  if (listing.score !== null) lines.push(`Score: ${listing.score}`);
  if (listing.foundAt) lines.push(`Found at: ${listing.foundAt}`);
  if (listing.flags && listing.flags.length > 0) {
    lines.push(`Flags: ${listing.flags.join(", ")}`);
  }
  if (listing.statMods && Object.keys(listing.statMods).length > 0) {
    const stats = Object.entries(listing.statMods)
      .map(([k, v]) => `${k} ${v > 0 ? "+" : ""}${v}`)
      .join(", ");
    lines.push(`Stats: ${stats}`);
  }
  if (listing.resistMods && Object.keys(listing.resistMods).length > 0) {
    const resists = Object.entries(listing.resistMods)
      .map(([k, v]) => `${k} ${v > 0 ? "+" : ""}${v}`)
      .join(", ");
    lines.push(`Resists: ${resists}`);
  }
  if (listing.enchantMods && listing.enchantMods.length > 0) {
    const enchants = listing.enchantMods
      .map(e => `${e.enchant}: ${e.stat} +${e.amount}`)
      .join(", ");
    lines.push(`Enchants: ${enchants}`);
  }
  return lines.length > 0 ? lines.join("\n") : undefined;
};

// No React state involved - a brief CSS class flash on the clicked button
// is enough feedback and keeps this a plain module-level handler instead
// of needing the columns array turned into something state-aware.
const handleCopyLb = (num: number) => (e: MouseEvent<HTMLButtonElement>) => {
  e.stopPropagation();
  navigator.clipboard.writeText(`lb ${num}`);
  const btn = e.currentTarget;
  btn.classList.add("market-copy-lb-btn--copied");
  setTimeout(() => btn.classList.remove("market-copy-lb-btn--copied"), 1200);
};

const columnHelper = createColumnHelper<MarketListing>();

type TableMeta = { now: number };

const itemColumn = columnHelper.accessor("itemNamePlain", {
  header: "Item",
  cell: info => {
    const listing = info.row.original;
    const tooltip = buildCardTooltip(listing);
    return (
      <span className="d-inline-flex align-items-center">
        {isHot(listing) && (
          <span
            className="market-hot-badge market-tooltip"
            data-tooltip={`${listing.bidCount} bids so far`}
          >
            <Flame size={16} strokeWidth={2.5} />
            {listing.bidCount}
          </span>
        )}
        <span className="market-item-name" title={tooltip}>
          {listing.itemNameHtml ? (
            <span dangerouslySetInnerHTML={{ __html: listing.itemNameHtml }} />
          ) : (
            listing.itemNamePlain || "-"
          )}
        </span>
        <DamageBadge listing={listing} />
        {isEndingSoon(listing, (info.table.options.meta as TableMeta).now) && (
          <span className="market-ending-badge">Ending soon</span>
        )}
        {listing.itemNamePlain && (
          <Link
            to={`/market/item?name=${encodeURIComponent(listing.itemNamePlain)}`}
            className="market-item-stats-link"
            title="View price history"
            onClick={e => e.stopPropagation()}
          >
            <TrendingUp size={16} strokeWidth={2.5} />
          </Link>
        )}
        <button
          type="button"
          className="market-copy-lb-btn"
          title={`Copy "lb ${listing.num}"`}
          onClick={handleCopyLb(listing.num)}
        >
          <Copy size={14} strokeWidth={2.5} />
        </button>
      </span>
    );
  }
});

const levelColumn = columnHelper.accessor("level", {
  header: "Level",
  cell: info => info.getValue() ?? "-"
});

// Seller is secondary info: muted, smaller, and cut off with the full name
// on hover.
const sellerColumn = columnHelper.accessor("seller", {
  header: "Seller",
  cell: info => {
    const seller = info.getValue();
    return (
      <span className="market-seller" title={seller || undefined}>
        {seller || "-"}
      </span>
    );
  }
});

// Column-specific widths are keyed off this class (not :first-child).
const columnClass = (id: string) =>
  id === "itemNamePlain" ? "market-col-item" : undefined;

const isHot = (listing: MarketListing) =>
  listing.status === "listed" && (listing.bidCount ?? 0) >= HOT_BID_COUNT;

const liveColumns = [
  itemColumn,
  levelColumn,
  sellerColumn,
  columnHelper.accessor("lastBidAmount", {
    header: "Current Bid",
    cell: info => {
      const listing = info.row.original;
      const hasBuyout = !!listing.buyoutPrice;
      return (
        <div className="market-current-bid-content">
          <span>
            {listing.lastBidAmount ? (
              <>
                <Price amount={listing.lastBidAmount} currency={listing.currency} />
                {` (${listing.lastBidder})`}
              </>
            ) : (
              <span className="market-muted">No bids</span>
            )}
            {(listing.bidCount ?? 0) > 0 && (
              <span className="market-muted">
                {` · ${listing.bidCount} bid${listing.bidCount === 1 ? "" : "s"}`}
              </span>
            )}
          </span>
          {hasBuyout && (
            <div className="market-buyout-line">
              {"Buyout: "}
              <Price amount={listing.buyoutPrice} currency={listing.currency} />
            </div>
          )}
        </div>
      );
    }
  }),
  columnHelper.accessor("endsAt", {
    header: "Time left",
    cell: info => {
      const now = (info.table.options.meta as TableMeta).now;
      const { text, urgent } = formatTimeLeft(info.getValue(), now);
      return (
        <span
          className={urgent ? "market-time-left market-time-left--urgent" : "market-time-left"}
          title={formatDateTime(info.getValue())}
        >
          {text}
        </span>
      );
    }
  }),
  columnHelper.accessor("listedAt", {
    header: "Listed",
    cell: info => (
      <span className="market-muted market-date">{formatDateTime(info.getValue())}</span>
    )
  })
];

const historyColumns = [
  itemColumn,
  levelColumn,
  sellerColumn,
  columnHelper.accessor("soldTo", {
    header: "Buyer",
    // Brighter than the seller: who won matters more than who sold.
    cell: info => (
      <span className="market-buyer" title={info.getValue() || undefined}>
        {info.getValue() || "-"}
      </span>
    )
  }),
  columnHelper.accessor("soldPrice", {
    header: "Final price",
    cell: info => {
      const listing = info.row.original;
      if (listing.status === "sold") {
        return (
          <span title={`${listing.soldPrice?.toLocaleString()} ${listing.currency || ""}`}>
            <Price amount={listing.soldPrice} currency={listing.currency} />
          </span>
        );
      }
      // Expired rows can still carry the bids the client saw, just not how
      // the auction ended.
      return listing.lastBidAmount ? (
        <span className="market-muted">
          {"Top bid "}
          <Price amount={listing.lastBidAmount} currency={listing.currency} />
        </span>
      ) : (
        <span className="market-muted">No bids</span>
      );
    }
  }),
  columnHelper.accessor("status", {
    header: "Outcome",
    cell: info =>
      info.getValue() === "sold" ? (
        <span className="market-status-badge market-status-sold">Sold</span>
      ) : (
        // `stale` means the plugin lost track of how the auction ended, which
        // the FE treats the same as expired.
        <span className="market-status-badge market-status-expired">Expired</span>
      )
  }),
  columnHelper.accessor("concludedAt", {
    header: "Concluded",
    cell: info => formatDateTime(info.getValue())
  })
];

type Props = {
  view: MarketView;
  data: MarketListing[];
  pagination: PaginationMetadata;
  onPageChange: (page: number) => void;
};

const MarketTable = ({ view, data, pagination, onPageChange }: Props) => {
  // Sorting is server-side (the sort buttons in Market.tsx), so the table
  // renders rows in the order they arrive.
  const [expandedNum, setExpandedNum] = useState<number | null>(null);

  // Ticks so the "Time left" countdown keeps moving between data refreshes.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (view !== "live") return undefined;
    const interval = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(interval);
  }, [view]);

  const columns = view === "live" ? liveColumns : historyColumns;

  const table = useReactTable({
    data,
    columns: columns as typeof liveColumns,
    getCoreRowModel: getCoreRowModel(),
    meta: { now } as TableMeta
  });

  return (
    <div>
      <div className="market-table-wrap">
        <table className={`market-table market-table--${view}`}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id} className={columnClass(header.column.id)}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No results
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => {
                const listing = row.original;
                const isExpanded = expandedNum === listing.num;
                return (
                  <Fragment key={row.id}>
                    <tr
                      className={`market-row-clickable${
                        isEndingSoon(listing, now) ? " market-row-ending" : ""
                      }`}
                      onClick={() =>
                        setExpandedNum(isExpanded ? null : listing.num)
                      }
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className={
                            cell.column.id === "lastBidAmount"
                              ? "market-current-bid-cell"
                              : `${columnClass(cell.column.id) ?? ""}${
                                  cell.column.id === "itemNamePlain" && isHot(listing)
                                    ? " market-col-item--hot"
                                    : ""
                                }` || undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={columns.length} className="market-detail-cell">
                          {listing.rawCaptureHtml ? (
                            <pre
                              className="market-raw-capture"
                              dangerouslySetInnerHTML={{
                                __html: listing.rawCaptureHtml
                              }}
                            />
                          ) : (
                            <span className="text-muted">
                              No captured item card for this listing.
                            </span>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Live shows every current listing on one page. */}
      {view === "history" && data.length > 0 && (
        <PaginationControls pagination={pagination} onPageChange={onPageChange} />
      )}
    </div>
  );
};

export default MarketTable;
